import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  FinanceUnit,
  Product,
  Surcharge,
  InvoiceLine,
  PricingAgreement,
  financeDataT,
  pricingAgreementsT,
  createUpdateLineT,
  linesT,
  linesWithRevisionT,
  CreateUpdateInvoiceLine,
} from "./types";
import api, { createDecoder } from "../../api";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { RootState } from "../../../redux-store";
import { selectFeature } from "../../features/selectors";

// The set of invoice lines a driver sees depends on product visibility
// (visibleToDrivers), which can change WITHOUT bumping the delivery revision —
// so no socket nudge fires for it. Detect such product changes (a product
// added/removed, or any product's visibleToDrivers flipped) so we can refetch
// invoice lines when the socket can't tell us to.
const productsAffectingInvoiceLinesChanged = (
  before: Product[],
  after: Product[]
): boolean => {
  if (before.length !== after.length) {
    return true;
  }
  const beforeById = new Map(before.map((p) => [p.id, p]));
  return after.some((p) => {
    const prev = beforeById.get(p.id);
    return !prev || prev.visibleToDrivers !== p.visibleToDrivers;
  });
};

type State = {
  financeData: {
    requestState: "idle" | "loading" | "success" | "error";
    products: Product[];
    surcharges: Surcharge[];
    units: FinanceUnit[];
  };
  pricingAgreements: {
    requestState: "idle" | "loading" | "success" | "error";
    data: PricingAgreement[];
  };
  invoiceLines: {
    requestState: "idle" | "loading" | "success" | "error";
    cargosLines: {
      [deliveryId: string]: InvoiceLine[];
    };
    cargosDeliveriesRevisions: {
      [deliveryId: string]: string;
    };
  };
};

const initialState: State = {
  financeData: {
    requestState: "idle",
    products: [],
    surcharges: [],
    units: [],
  },
  pricingAgreements: {
    data: [],
    requestState: "idle",
  },
  invoiceLines: {
    cargosLines: {},
    cargosDeliveriesRevisions: {},
    requestState: "idle",
  },
};

const prefix = "data/finance";

export const loadFinanceDataForDriver = createAsyncThunk(
  `${prefix}/load-finance-data-for-driver`,
  async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const previousProducts = state.data.finance.financeData.products;
    // const loadingState = selectFinanceDataLoadingState(state);
    // TODO; this is set immediately, you can't check it in the action, refactor
    // if (loadingState === "loading") {
    //   return;
    // }
    const result = await api(thunkAPI).get({
      url: "/drivers-api/finance",
      decoder: createDecoder(financeDataT),
    });

    // In websocket mode invoice lines aren't polled, so a product/visibility
    // change (which doesn't bump the delivery revision → no socket nudge) would
    // be missed. The finance data IS still polled, so use it as the signal:
    // when the visible product set changes, refetch invoice lines for the
    // deliveries currently in view.
    if (
      selectFeature("mobile-app-invoice-lines-websockets")(state) &&
      productsAffectingInvoiceLinesChanged(previousProducts, result.products)
    ) {
      Object.keys(
        state.data.finance.invoiceLines.cargosDeliveriesRevisions
      ).forEach((deliveryId) =>
        thunkAPI.dispatch(loadInvoiceLinesWithRevision({ deliveryId }))
      );
    }

    return result;
  }
);

export const loadPricingAgreements = createAsyncThunk(
  `${prefix}/load-pricing-agreements`,
  async (input: { deliveryId: string }, thunkAPI) => {
    const { deliveryId } = input;
    return api(thunkAPI).get({
      url: `/drivers-api/finance/${deliveryId}/pricing-agreements`,
      decoder: createDecoder(pricingAgreementsT),
    });
  }
);

export const loadInvoiceLines = createAsyncThunk(
  `${prefix}/load-invoice-lines`,
  async (input: { deliveryId: string }, thunkAPI) => {
    const { deliveryId } = input;
    return api(thunkAPI).get({
      url: `/drivers-api/finance/${deliveryId}/invoice-lines`,
      decoder: createDecoder(linesT),
    });
  }
);

export const loadInvoiceLinesWithRevision = createAsyncThunk(
  `${prefix}/load-invoice-lines-with-revision`,
  async (input: { deliveryId: string }, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState() as RootState;
    const { deliveryId } = input;
    const knownDeliveryRevisionNumber =
      state.data.finance.invoiceLines.cargosDeliveriesRevisions[deliveryId] ||
      null;
    return api(thunkAPI).get({
      url: `/drivers-api/finance/${deliveryId}/invoice-lines-with-revision${knownDeliveryRevisionNumber ? `?deliveryRevisionNumber=${knownDeliveryRevisionNumber}` : ""}`,
      decoder: createDecoder(linesWithRevisionT),
    });
  }
);

export const createInvoiceLine = createErrorReportingAsyncThunk(
  `${prefix}/create-invoice-line`,
  async (
    input: { deliveryId: string; line: CreateUpdateInvoiceLine },
    thunkAPI
  ) => {
    const { deliveryId, line } = input;
    return api(thunkAPI).post({
      url: `/drivers-api/finance/${deliveryId}/invoice-lines`,
      body: createUpdateLineT.encode(line),
      decoder: createDecoder(linesT),
    });
  }
);

export const updateInvoiceLine = createErrorReportingAsyncThunk(
  `${prefix}/update-invoice-line`,
  async (
    input: {
      deliveryId: string;
      lineId: string;
      line: CreateUpdateInvoiceLine;
    },
    thunkAPI
  ) => {
    const { deliveryId, lineId, line } = input;
    return api(thunkAPI).put({
      url: `/drivers-api/finance/${deliveryId}/invoice-lines/${lineId}`,
      body: createUpdateLineT.encode(line),
      decoder: createDecoder(linesT),
    });
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadFinanceDataForDriver.pending, (state) => {
      state.financeData.requestState = "loading";
    });
    builder.addCase(loadFinanceDataForDriver.rejected, (state) => {
      state.financeData.requestState = "error";
    });
    builder.addCase(loadFinanceDataForDriver.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }
      state.financeData.requestState = "success";
      state.financeData.products = action.payload.products;
      state.financeData.surcharges = action.payload.surcharges;
      state.financeData.units = action.payload.units;
    });
    builder.addCase(loadInvoiceLines.pending, (state) => {
      state.invoiceLines.requestState = "loading";
    });
    builder.addCase(loadInvoiceLines.fulfilled, (state, action) => {
      state.invoiceLines.requestState = "success";
      state.invoiceLines.cargosLines[action.meta.arg.deliveryId] = action.payload;
    });
    builder
      .addCase(loadInvoiceLinesWithRevision.pending, (state) => {
        state.invoiceLines.requestState = "loading";
      })
      .addCase(loadInvoiceLinesWithRevision.fulfilled, (state, action) => {
        if (action.payload.hasChanged) {
          state.invoiceLines.cargosLines[action.meta.arg.deliveryId] =
            action.payload.lines!;
        }
        state.invoiceLines.cargosDeliveriesRevisions[
          action.meta.arg.deliveryId
        ] = action.payload.revisionNumber;
      });
    builder.addCase(createInvoiceLine.fulfilled, (state, action) => {
      state.invoiceLines.cargosLines[action.meta.arg.deliveryId] =
        action.payload;
    });
    builder.addCase(updateInvoiceLine.fulfilled, (state, action) => {
      state.invoiceLines.cargosLines[action.meta.arg.deliveryId] =
        action.payload;
    });
    builder.addCase(loadPricingAgreements.fulfilled, (state, action) => {
      state.pricingAgreements.requestState = "success";
      state.pricingAgreements.data = action.payload;
    });
  },
});

export default slice.reducer;
